import React, { useCallback, useMemo, useState } from "react";

import { Divider } from "antd";
import { Col, Row } from "antd/es/grid";
import Popover from "antd/es/popover";
import Paragraph from "antd/es/typography/Paragraph";
import { Editor, Transforms } from "slate";
import { ReactEditor, useSlate } from "slate-react";
import { RenderElementProps } from "slate-react/dist/components/editable";

import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import Button from "../../../../Button";
import { useConfigProvider } from "../../../../ConfigProvider";
import { LinkElement } from "../../../../type/slate-types";
import LinkModal from "../LinkModal";

import styles from "./Link.module.scss";

interface LinkProps {
  attributes: RenderElementProps["attributes"];
  children: React.ReactNode;
  element: LinkElement;
  readOnly?: boolean;
}

const Link = ({ attributes, children, element, readOnly }: LinkProps) => {
  const editor = useSlate();
  const configProvider = useConfigProvider();

  const [editModalVisible, setEditModalVisibility] = useState(false);
  const [popoverVisible, setPopoverVisibility] = useState(false);

  const path = ReactEditor.findPath(editor, element);

  const handleUpdate = useDynamicCallback((text: string, url: string) => {
    Transforms.setNodes(editor, { url }, { at: path });
    Transforms.delete(editor, {
      at: [...path, 0],
    });
    Transforms.insertText(editor, text, { at: [...path, 0] });
    setEditModalVisibility(false);
  });

  const handleRemove = useCallback(() => {
    const path = ReactEditor.findPath(editor, element);
    Transforms.removeNodes(editor, { at: path });
  }, [editor, element]);

  const content = useMemo(
    () => (
      <Row align="middle">
        <Col>
          <span className={styles.toolbarLinkContainer}>
            <span className={styles.toolbarLinkLabel}>
              {configProvider.formatMessage("ui.emailBody.linkModal.goToLink")}
            </span>
            <Paragraph
              className={styles.toolbarLinkUrl}
              ellipsis={{
                tooltip: {
                  placement: "bottom",
                  title: element.url,
                },
              }}
            >
              <a href={element.url} rel="noreferrer" target="_blank">
                {element.url}
              </a>
            </Paragraph>
          </span>
        </Col>
        <Col>
          <Divider className={styles.divider} type="vertical" />
        </Col>
        <Col>
          <Button
            onClick={() => {
              setPopoverVisibility(false);
              setEditModalVisibility(true);
            }}
            size="small"
            type="link"
          >
            {configProvider.formatMessage("ui.emailBody.linkModal.change")}
          </Button>
        </Col>
        <Col>
          <Divider className={styles.divider} type="vertical" />
        </Col>
        <Col>
          <Button danger onClick={handleRemove} size="small" type="link">
            {configProvider.formatMessage("ui.emailBody.linkModal.remove")}
          </Button>
        </Col>
      </Row>
    ),
    [configProvider, element, setPopoverVisibility, setEditModalVisibility, handleRemove]
  );

  return (
    <>
      <Popover
        content={content}
        onOpenChange={setPopoverVisibility}
        open={popoverVisible}
        overlayClassName={styles.popover}
        placement="bottom"
        showArrow={false}
        trigger={readOnly ? [] : ["click"]}
      >
        <span {...attributes} className={styles.link}>
          {children}
        </span>
      </Popover>
      {editModalVisible && (
        <LinkModal
          initialText={Editor.string(editor, path)}
          initialUrl={element.url}
          onHide={() => setEditModalVisibility(false)}
          onOk={handleUpdate}
        />
      )}
    </>
  );
};

export default Link;
